import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdDownload, MdOpenInFull, MdClose, MdAutoFixHigh } from 'react-icons/md';
import PageHeader from '../common/PageHeader';
import TearSheetPanel from './TearSheetPanel';
import { useTearSheet } from '../../hooks/useTearSheet';

const TearSheet: React.FC = () => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedPreview, setUploadedPreview] = useState<string | null>(null);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const tearSheet = useTearSheet({
    onGenerated: () => setGalleryIndex(1),
  });

  const handleFileSelect = useCallback((file: File) => {
    setUploadedFile(file);
    setUploadedPreview(URL.createObjectURL(file));
    tearSheet.reset();
    setGalleryIndex(0);
  }, [tearSheet.reset]);

  const handleFileRemove = useCallback(() => {
    if (uploadedPreview) URL.revokeObjectURL(uploadedPreview);
    setUploadedFile(null);
    setUploadedPreview(null);
    tearSheet.reset();
    setGalleryIndex(0);
  }, [uploadedPreview, tearSheet.reset]);

  const handleGenerate = useCallback(() => {
    if (!uploadedFile) return;
    tearSheet.generateFromFile(uploadedFile);
  }, [uploadedFile, tearSheet.generateFromFile]);

  const galleryImages = useMemo(() => {
    const imgs: { url: string; label: string }[] = [];
    if (uploadedPreview) imgs.push({ url: uploadedPreview, label: 'Original Upload' });
    if (tearSheet.generatedImageUrl) imgs.push({ url: tearSheet.generatedImageUrl, label: 'Generated Tear Sheet' });
    return imgs;
  }, [uploadedPreview, tearSheet.generatedImageUrl]);

  const handleDownload = async (imageUrl: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tear-sheet.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { window.open(imageUrl, '_blank'); }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const parsedDims = (() => {
    if (!tearSheet.dimensions.trim()) return { w: '', h: '', d: '' };
    const t = tearSheet.dimensions.trim();
    const wM = t.match(/([\d.]+)["'']?\s*W/i);
    const hM = t.match(/([\d.]+)["'']?\s*H/i);
    const dM = t.match(/([\d.]+)["'']?\s*D/i);
    return { w: wM ? `${wM[1]}"` : '', h: hM ? `${hM[1]}"` : '', d: dM ? `${dM[1]}"` : '' };
  })();

  const displayImage = tearSheet.generatedImageUrl || uploadedPreview;

  return (
    <div className="h-[calc(100vh-20px)] flex flex-col">
      <PageHeader title="Tear Sheet" showBackButton={false} />

      <div className="flex-1 flex gap-3 min-h-0 mt-2 mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="w-[280px] min-w-[260px] flex-shrink-0 rounded-xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, rgba(20, 22, 28, 0.98), rgba(16, 18, 22, 0.99))',
            border: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          <TearSheetPanel
            uploadedFile={uploadedFile}
            uploadedPreview={uploadedPreview}
            dimensions={tearSheet.dimensions}
            isGenerating={tearSheet.isGenerating}
            generatedImageUrl={tearSheet.generatedImageUrl}
            error={tearSheet.error}
            onFileSelect={handleFileSelect}
            onFileRemove={handleFileRemove}
            onDimensionsChange={tearSheet.setDimensions}
            onGenerate={handleGenerate}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.06 }}
          className="flex-1 min-w-0"
        >
          <div
            className="h-full rounded-xl overflow-hidden relative group"
            style={{
              background: '#fafafa',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              border: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            {tearSheet.generatedImageUrl ? (
              <div className="w-full h-full flex flex-col relative">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-[11px] font-semibold text-gray-700">Generated Tear Sheet</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {galleryImages.length > 1 && galleryImages.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setGalleryIndex(idx)}
                        className={`px-2 py-0.5 rounded text-[9px] font-medium transition-all cursor-pointer ${
                          galleryIndex === idx
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {img.label}
                      </button>
                    ))}
                    <button
                      onClick={() => handleDownload(galleryImages[galleryIndex]?.url || tearSheet.generatedImageUrl!)}
                      className="ml-1 w-6 h-6 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-all cursor-pointer"
                      title="Download"
                    >
                      <MdDownload size={13} />
                    </button>
                    <button
                      onClick={() => setIsFullscreen(true)}
                      className="w-6 h-6 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-all cursor-pointer"
                      title="Fullscreen"
                    >
                      <MdOpenInFull size={11} />
                    </button>
                  </div>
                </div>
                <div className="flex-1 flex items-center justify-center p-3 min-h-0 bg-gradient-to-br from-[#f5f3f0] to-[#eae7e3]">
                  <img
                    src={galleryImages[galleryIndex]?.url || tearSheet.generatedImageUrl}
                    alt="Tear Sheet"
                    className="max-w-full max-h-full object-contain rounded-lg"
                    style={{ filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.08))' }}
                  />
                </div>
              </div>
            ) : uploadedPreview ? (
              <div className="w-full h-full flex flex-col relative">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)' }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5">
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="M2 17l10 5 10-5" />
                        <path d="M2 12l10 5 10-5" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-sm font-bold text-gray-900 leading-none">Product Specification</h2>
                      <p className="text-[9px] text-gray-400 uppercase tracking-[0.1em] mt-0.5">Tear Sheet Preview</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {parsedDims.w && (
                      <span className="px-2 py-0.5 rounded bg-gray-100 text-[9px] font-semibold text-gray-600">
                        {parsedDims.w} W
                      </span>
                    )}
                    {parsedDims.h && (
                      <span className="px-2 py-0.5 rounded bg-gray-100 text-[9px] font-semibold text-gray-600">
                        {parsedDims.h} H
                      </span>
                    )}
                    {parsedDims.d && (
                      <span className="px-2 py-0.5 rounded bg-gray-100 text-[9px] font-semibold text-gray-600">
                        {parsedDims.d} D
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex-1 flex items-center justify-center p-4 min-h-0">
                  <div className="relative inline-flex items-center justify-center" style={{ maxWidth: '75%', maxHeight: '100%' }}>
                    {parsedDims.w && (
                      <div className="absolute -top-3 left-[3%] right-[3%] flex flex-col items-center pointer-events-none z-10">
                        <span className="text-[10px] font-semibold text-gray-700 mb-0.5">{parsedDims.w} W</span>
                        <div className="w-full flex items-center">
                          <div className="w-px h-2 bg-gray-700" />
                          <div className="flex-1 h-px bg-gray-700" />
                          <div className="w-px h-2 bg-gray-700" />
                        </div>
                      </div>
                    )}
                    {parsedDims.h && (
                      <div className="absolute -right-10 top-[5%] bottom-[3%] flex items-center pointer-events-none z-10">
                        <div className="h-full flex flex-col items-center">
                          <div className="h-px w-2 bg-gray-700" />
                          <div className="flex-1 w-px bg-gray-700" />
                          <div className="h-px w-2 bg-gray-700" />
                        </div>
                        <span className="text-[10px] font-semibold text-gray-700 ml-1 whitespace-nowrap" style={{ writingMode: 'vertical-rl' }}>
                          {parsedDims.h} H
                        </span>
                      </div>
                    )}
                    <img
                      src={uploadedPreview}
                      alt="Product"
                      className="max-w-full max-h-[48vh] object-contain rounded"
                      style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.05))' }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between px-5 py-2 border-t border-gray-100">
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-[9px] font-medium text-gray-400">Live Preview</span>
                  </div>
                  {tearSheet.dimensions.trim() && !parsedDims.w && !parsedDims.h && (
                    <span className="text-[9px] text-gray-400 truncate max-w-[200px]">{tearSheet.dimensions.trim()}</span>
                  )}
                </div>

                <AnimatePresence>
                  {tearSheet.isGenerating && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-xl"
                      style={{ background: 'rgba(250,250,250,0.94)', backdropFilter: 'blur(8px)' }}
                    >
                      <div className="relative w-10 h-10 mb-3">
                        <div className="absolute inset-0 rounded-full border-2 border-gray-200" />
                        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#EE4423] animate-spin" />
                      </div>
                      <p className="text-xs font-semibold text-gray-700">Generating...</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">This may take a moment</p>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <button
                    onClick={() => setIsFullscreen(true)}
                    className="w-7 h-7 rounded-lg bg-black/30 backdrop-blur flex items-center justify-center text-white/80 hover:bg-black/50 transition-all cursor-pointer"
                  >
                    <MdOpenInFull size={12} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center px-6">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-white border border-gray-100 flex items-center justify-center shadow-sm">
                    <MdAutoFixHigh size={22} className="text-gray-300" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-1">Create Your Tear Sheet</h3>
                  <p className="text-[11px] text-gray-400 leading-relaxed max-w-xs mx-auto">
                    Upload a product image and enter dimensions to generate a professional specification sheet.
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-4">
                    {['Upload', 'Dimensions', 'Generate'].map((step, i) => (
                      <React.Fragment key={step}>
                        {i > 0 && <div className="w-4 h-px bg-gray-200" />}
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-4 rounded-full bg-[#EE4423]/10 flex items-center justify-center">
                            <span className="text-[8px] font-bold text-[#EE4423]">{i + 1}</span>
                          </div>
                          <span className="text-[10px] font-medium text-gray-400">{step}</span>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {isFullscreen && displayImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 backdrop-blur-lg flex items-center justify-center"
            onClick={() => setIsFullscreen(false)}
          >
            <button
              onClick={() => setIsFullscreen(false)}
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center cursor-pointer z-20"
            >
              <MdClose size={20} className="text-white" />
            </button>
            <motion.img
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              src={displayImage}
              alt="Fullscreen"
              className="max-w-[92vw] max-h-[92vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
              draggable={false}
            />
            {tearSheet.generatedImageUrl && (
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2">
                <button
                  onClick={(e) => { e.stopPropagation(); handleDownload(tearSheet.generatedImageUrl!); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur text-white/80 hover:text-white text-[11px] font-medium transition-all cursor-pointer"
                >
                  <MdDownload size={14} /> Download
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TearSheet;
